import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import {
  calendarEventCreateRequest,
  calendarEventPatch,
  initialCalendarEventForm,
  isCalendarEventFormValid,
  isCalendarEventStartOnPastDay,
  toggleCalendarEventTemporalMode,
  validateCalendarEventForm,
  type CalendarEventFormErrors,
  type CalendarEventFormState,
} from "calendar/calendar-event-form";
import {
  useCreateCalendarEventMutation,
  useLazyGetCalendarEventByIdQuery,
  usePatchCalendarEventMutation,
} from "calendar/_api/calendar-event-api";
import {
  calendarWeekdays,
  type CalendarEventResponse,
  type CalendarRecurrence,
} from "calendar/_schemas/calendar-event-schema";
import {
  calendarEventFieldLimits,
  calendarWeekdayAbbreviationLength,
} from "calendar/calendar-event-fields";
import { toLocalDateInput } from "_shared/dates/date-input";
import { getErrorMessageFromUnknown } from "_shared/_errors/api-error-message";
import { refreshStaleStateOnce } from "_shared/_errors/stale-state";
import { readCalendarEventFailure } from "calendar/calendar-event-error";
import moment, { type Moment } from "moment";
import { useState, type FormEvent } from "react";

type CalendarEventEditorProps = {
  defaultDate: string;
  event?: CalendarEventResponse;
  onCancel: () => void;
  onSaved: (mode: "created" | "updated") => void;
};

const EditorForm = styled("form")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  paddingTop: theme.spacing(1.5),
}));
const DateFields = styled(Box)(({ theme }) => ({ display: "grid", gridTemplateColumns: "1fr", gap: theme.spacing(2), [theme.breakpoints.up("sm")]: { gridTemplateColumns: "1fr 1fr" } }));
const RecurrencePanel = styled(Box)(({ theme }) => ({ border: `1px solid ${theme.palette.divider}`, borderRadius: theme.shape.borderRadius, padding: theme.spacing(2), display: "flex", flexDirection: "column", gap: theme.spacing(2) }));
const RecurrenceFields = styled(Box)(({ theme }) => ({ display: "grid", gridTemplateColumns: "1fr", gap: theme.spacing(2), [theme.breakpoints.up("sm")]: { gridTemplateColumns: "2fr 1fr" } }));
const EditorActions = styled(Box)(({ theme }) => ({ display: "flex", justifyContent: "flex-end", gap: theme.spacing(1) }));
const pastStartDayError = "New events cannot start on a past date.";
const dateFormat = "YYYY-MM-DD";
const dateTimeFormat = "YYYY-MM-DDTHH:mm";
const relatedLinkHelperText = "Enter a URL related to this event (e.g., https://example.com).";

function pickerDate(value: string): Moment | null {
  return value ? moment(value, dateFormat, true) : null;
}

function pickerDateTime(value: string): Moment | null {
  return value ? moment(value, dateTimeFormat, true) : null;
}

function pickerValue(value: Moment | null, format: string): string {
  return value?.isValid() ? value.format(format) : "";
}

export default function CalendarEventEditor({ defaultDate, event, onCancel, onSaved }: Readonly<CalendarEventEditorProps>) {
  const [currentEvent, setCurrentEvent] = useState(event);
  const [form, setForm] = useState(() => initialCalendarEventForm(defaultDate, event));
  const [errors, setErrors] = useState<CalendarEventFormErrors>({});
  const [apiError, setApiError] = useState<string>();
  const [createEvent, createState] = useCreateCalendarEventMutation();
  const [patchEvent, patchState] = usePatchCalendarEventMutation();
  const [refreshEvent, refreshState] = useLazyGetCalendarEventByIdQuery();
  const isSaving = createState.isLoading || patchState.isLoading || refreshState.isFetching;
  const isReadOnly = Boolean(currentEvent?.isSourceManaged || currentEvent && !currentEvent.canEdit);
  const referenceDate = new Date();
  const earliestNewEventDate = event ? undefined : toLocalDateInput(referenceDate);
  const startsOnPastDay = !event && isCalendarEventStartOnPastDay(form, referenceDate);
  const isFormValid = isCalendarEventFormValid(form) && !startsOnPastDay;

  const update = <K extends keyof CalendarEventFormState>(field: K, value: CalendarEventFormState[K]) => {
    setForm((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: undefined }));
  };

  const handleSubmit = async (submitEvent: FormEvent) => {
    submitEvent.preventDefault();
    const nextErrors = validateCalendarEventForm(form);
    if (startsOnPastDay) {
      if (form.isAllDay && !nextErrors.startDate) nextErrors.startDate = pastStartDayError;
      else if (!form.isAllDay && !nextErrors.startDateTime) nextErrors.startDateTime = pastStartDayError;
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setApiError(undefined);
    try {
      if (currentEvent) {
        await patchEvent({ eventId: currentEvent.id, patch: calendarEventPatch(form) }).unwrap();
        onSaved("updated");
      } else {
        await createEvent(calendarEventCreateRequest(form)).unwrap();
        onSaved("created");
      }
    } catch (error) {
      const failure = readCalendarEventFailure(error);
      setErrors((current) => ({ ...current, ...failure.fieldErrors }));

      if (currentEvent && (failure.apiError.status === 409 || failure.apiError.status === 412)) {
        try {
          await refreshStaleStateOnce(error, `calendar.patch:${currentEvent.id}`, async () => {
            const freshEvent = await refreshEvent({ eventId: currentEvent.id }, false).unwrap();
            setCurrentEvent(freshEvent);
            setForm(initialCalendarEventForm(defaultDate, freshEvent));
            setErrors({});
          });
        } catch {
          // Keep the original visible conflict. The failed read must never cause the write to replay.
        }
      }

      setApiError(failure.message
        ?? getErrorMessageFromUnknown(error)
        ?? "The calendar event could not be saved. Please try again.");
    }
  };

  return (
    <EditorForm onSubmit={handleSubmit} noValidate>
      {currentEvent?.recurrence && <Alert severity="info">Editing this event changes the entire recurring series.</Alert>}
      {isReadOnly && !apiError && <Alert severity="warning">This calendar event is read-only. Refresh the event to see its current state.</Alert>}
      {apiError && <Alert severity="error">{apiError}</Alert>}
      <TextField
        required
        label="Title"
        value={form.title}
        onChange={(changeEvent) => update("title", changeEvent.target.value)}
        error={Boolean(errors.title)}
        helperText={errors.title ?? `${form.title.length}/${calendarEventFieldLimits.title}`}
        slotProps={{ htmlInput: { maxLength: calendarEventFieldLimits.title } }}
      />
      <FormControlLabel
        control={<Checkbox checked={form.isAllDay} onChange={(changeEvent) => setForm((current) => toggleCalendarEventTemporalMode(current, changeEvent.target.checked))} />}
        label="All-day event"
      />
      {form.isAllDay ? (
        <DateFields>
          <DatePicker
            label="Start date"
            value={pickerDate(form.startDate)}
            minDate={pickerDate(earliestNewEventDate ?? "") ?? undefined}
            onChange={(value) => update("startDate", pickerValue(value, dateFormat))}
            slotProps={{
              openPickerIcon: { color: "primary" },
              textField: {
                required: true,
                error: Boolean(errors.startDate) || startsOnPastDay,
                helperText: errors.startDate ?? (startsOnPastDay ? pastStartDayError : undefined),
              },
            }}
          />
          <DatePicker
            label="End date"
            value={pickerDate(form.endDate)}
            onChange={(value) => update("endDate", pickerValue(value, dateFormat))}
            slotProps={{
              openPickerIcon: { color: "primary" },
              textField: {
                required: true,
                error: Boolean(errors.endDate),
                helperText: errors.endDate ?? "Inclusive",
              },
            }}
          />
        </DateFields>
      ) : (
        <DateFields>
          <DateTimePicker
            label="Start"
            value={pickerDateTime(form.startDateTime)}
            minDate={pickerDate(earliestNewEventDate ?? "") ?? undefined}
            onChange={(value) => update("startDateTime", pickerValue(value, dateTimeFormat))}
            slotProps={{
              openPickerIcon: { color: "primary" },
              textField: {
                required: true,
                error: Boolean(errors.startDateTime) || startsOnPastDay,
                helperText: errors.startDateTime ?? (startsOnPastDay ? pastStartDayError : undefined),
              },
            }}
          />
          <DateTimePicker
            label="End"
            value={pickerDateTime(form.endDateTime)}
            onChange={(value) => update("endDateTime", pickerValue(value, dateTimeFormat))}
            slotProps={{
              openPickerIcon: { color: "primary" },
              textField: {
                error: Boolean(errors.endDateTime),
                helperText: errors.endDateTime,
              },
            }}
          />
        </DateFields>
      )}
      {!form.isAllDay && (
        <TextField
          required
          label="Time zone"
          value={form.timeZoneId}
          onChange={(changeEvent) => update("timeZoneId", changeEvent.target.value)}
          error={Boolean(errors.timeZoneId)}
          helperText={errors.timeZoneId}
        />
      )}
      <TextField
        multiline
        minRows={3}
        label="Description"
        value={form.description}
        onChange={(changeEvent) => update("description", changeEvent.target.value)}
        error={Boolean(errors.description)}
        helperText={errors.description ?? `${form.description.length}/${calendarEventFieldLimits.description}`}
        slotProps={{ htmlInput: { maxLength: calendarEventFieldLimits.description } }}
      />
      <TextField
        label="Related Link"
        value={form.link}
        onChange={(changeEvent) => update("link", changeEvent.target.value)}
        error={Boolean(errors.link)}
        helperText={errors.link ?? relatedLinkHelperText}
        slotProps={{ htmlInput: { maxLength: calendarEventFieldLimits.link } }}
      />
      <FormControlLabel
        control={<Checkbox checked={form.repeats} onChange={(changeEvent) => update("repeats", changeEvent.target.checked)} />}
        label="Repeat this event"
      />
      {form.repeats && (
        <RecurrencePanel>
          <Typography variant="subtitle1">Recurrence</Typography>
          <RecurrenceFields>
            <TextField
              select
              label="Frequency"
              value={form.frequency}
              onChange={(changeEvent) => update("frequency", changeEvent.target.value as CalendarRecurrence["frequency"])}
              error={Boolean(errors.frequency)}
              helperText={errors.frequency}
            >
              <MenuItem value="daily">Daily</MenuItem>
              <MenuItem value="weekly">Weekly</MenuItem>
              <MenuItem value="monthly">Monthly</MenuItem>
              <MenuItem value="yearly">Yearly</MenuItem>
            </TextField>
            <TextField
              type="number"
              label="Interval"
              value={form.interval}
              onChange={(changeEvent) => update("interval", changeEvent.target.value)}
              error={Boolean(errors.interval)}
              helperText={errors.interval}
              slotProps={{ htmlInput: { min: 1, step: 1 } }}
            />
          </RecurrenceFields>
          {form.frequency === "weekly" && (
            <Box>
              <Typography variant="subtitle2">Weekdays</Typography>
              <FormGroup row>
                {calendarWeekdays.map((weekday) => (
                  <FormControlLabel
                    key={weekday}
                    control={(
                      <Checkbox
                        checked={form.weekdays.includes(weekday)}
                        onChange={(changeEvent) => update(
                          "weekdays",
                          changeEvent.target.checked
                            ? [...form.weekdays, weekday]
                            : form.weekdays.filter((value) => value !== weekday),
                        )}
                      />
                    )}
                    label={weekday
                      .slice(0, calendarWeekdayAbbreviationLength)
                      .replace(/^./, (value) => value.toUpperCase())}
                  />
                ))}
              </FormGroup>
              {errors.weekdays && <Typography color="error" variant="caption">{errors.weekdays}</Typography>}
            </Box>
          )}
          <TextField select label="Ends" value={form.termination} onChange={(changeEvent) => update("termination", changeEvent.target.value as CalendarEventFormState["termination"])}>
            <MenuItem value="never">Never</MenuItem>
            <MenuItem value="count">After a number of occurrences</MenuItem>
            <MenuItem value="until">On a date</MenuItem>
          </TextField>
          {form.termination === "count" && (
            <TextField
              type="number"
              label="Occurrences"
              value={form.count}
              onChange={(changeEvent) => update("count", changeEvent.target.value)}
              error={Boolean(errors.count)}
              helperText={errors.count}
              slotProps={{ htmlInput: { min: 1, step: 1 } }}
            />
          )}
          {form.termination === "until" && (form.isAllDay ? (
            <DatePicker
              label="Repeat through"
              value={pickerDate(form.untilDate)}
              onChange={(value) => update("untilDate", pickerValue(value, dateFormat))}
              slotProps={{
                openPickerIcon: { color: "primary" },
                textField: {
                  error: Boolean(errors.untilDate),
                  helperText: errors.untilDate,
                },
              }}
            />
          ) : (
            <DateTimePicker
              label="Repeat through"
              value={pickerDateTime(form.untilDateTime)}
              onChange={(value) => update("untilDateTime", pickerValue(value, dateTimeFormat))}
              slotProps={{
                openPickerIcon: { color: "primary" },
                textField: {
                  error: Boolean(errors.untilDateTime),
                  helperText: errors.untilDateTime,
                },
              }}
            />
          ))}
        </RecurrencePanel>
      )}
      <EditorActions>
        <Button disabled={isSaving} onClick={onCancel}>Cancel</Button>
        <Button disabled={isSaving || isReadOnly || !isFormValid} type="submit" variant="contained">
          {isSaving ? "Saving…" : currentEvent ? "Save changes" : "Create event"}
        </Button>
      </EditorActions>
    </EditorForm>
  );
}
